import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Briefcase,
  Clock,
  GraduationCap,
  CheckCircle2,
  Sparkles,
  Send,
  Loader2,
  FileText,
  RefreshCw,
  ThumbsUp,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicJobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user, requireEmail } = useUser();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [approving, setApproving] = useState(false);
  const [optimization, setOptimization] = useState(null);
  const [showOptimization, setShowOptimization] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [optimizationStatus, setOptimizationStatus] = useState({
    attemptsUsed: 0,
    attemptsRemaining: 3,
    isApproved: false
  });

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  useEffect(() => {
    if (user?.email) {
      checkApplicationStatus();
      fetchCandidateProfile();
      fetchOptimizationStatus();
    }
  }, [user?.email, jobId]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API}/public/jobs/${jobId}`);
      setJob(response.data);
    } catch (error) {
      console.error("Error fetching job:", error);
      toast.error("Puesto no encontrado");
      navigate("/empleos");
    } finally {
      setLoading(false);
    }
  };

  const fetchCandidateProfile = async () => {
    try {
      const response = await axios.get(`${API}/candidate/profile/${encodeURIComponent(user.email)}`);
      setCandidateProfile(response.data);
    } catch (error) {
      setCandidateProfile(null);
    }
  };

  const fetchOptimizationStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/candidate/optimize-cv/${jobId}/status?email=${encodeURIComponent(user.email)}`
      );
      setOptimizationStatus({
        attemptsUsed: response.data.attempts_used,
        attemptsRemaining: response.data.attempts_remaining,
        isApproved: response.data.is_approved
      });
      if (response.data.optimization) {
        setOptimization(response.data.optimization);
      }
    } catch (error) {
      console.error("Error fetching optimization status:", error);
    }
  };

  const checkApplicationStatus = async () => {
    try {
      const response = await axios.get(`${API}/candidate/applications/${encodeURIComponent(user.email)}`);
      const applied = response.data.some(app => app.job_id === jobId);
      setHasApplied(applied);
    } catch (error) {
      console.error("Error checking application status:", error);
    }
  };

  const handleApply = async () => {
    if (!user) {
      requireEmail(() => handleApply());
      return;
    }

    if (!candidateProfile?.cv_data) {
      toast.error("Primero debes subir tu CV en tu perfil");
      navigate("/candidate/profile");
      return;
    }

    setApplying(true);
    try {
      await axios.post(`${API}/candidate/apply/${jobId}?email=${encodeURIComponent(user.email)}`);
      toast.success("¡Aplicación enviada exitosamente!");
      setHasApplied(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aplicar");
    } finally {
      setApplying(false);
    }
  };

  const handleOptimizeCV = async (regenerate = false) => {
    if (!user) {
      requireEmail(() => handleOptimizeCV(regenerate));
      return;
    }

    if (!candidateProfile?.cv_data) {
      toast.error("Primero debes subir tu CV en tu perfil");
      navigate("/candidate/profile");
      return;
    }

    // Check if we've reached the limit
    if (regenerate && optimizationStatus.attemptsRemaining <= 0) {
      toast.error("Has alcanzado el límite de 3 intentos de optimización");
      return;
    }

    setOptimizing(true);
    try {
      const response = await axios.post(
        `${API}/candidate/optimize-cv/${jobId}?email=${encodeURIComponent(user.email)}&regenerate=${regenerate}`
      );
      setOptimization(response.data.optimization);
      setOptimizationStatus({
        attemptsUsed: response.data.attempts_used,
        attemptsRemaining: response.data.attempts_remaining,
        isApproved: response.data.is_approved
      });
      setShowOptimization(true);
      
      if (regenerate) {
        toast.success(`Nueva optimización generada. Te quedan ${response.data.attempts_remaining} intentos.`);
      }
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error("Has alcanzado el límite de 3 intentos de optimización para este puesto");
      } else {
        toast.error(error.response?.data?.detail || "Error al optimizar CV");
      }
    } finally {
      setOptimizing(false);
    }
  };

  const handleApproveOptimization = async () => {
    setApproving(true);
    try {
      await axios.post(
        `${API}/candidate/optimize-cv/${jobId}/approve?email=${encodeURIComponent(user.email)}`
      );
      setOptimizationStatus(prev => ({ ...prev, isApproved: true, attemptsRemaining: 0 }));
      toast.success("¡Optimización aprobada y guardada!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aprobar la optimización");
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-muted rounded" />
        <div className="h-48 bg-muted rounded-2xl" />
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="space-y-6" data-testid="public-job-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/empleos")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Briefcase className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold">
                {job.title}
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Info */}
      <div className="flex flex-wrap gap-3">
        {job.min_years_experience > 0 && (
          <Badge variant="secondary" className="gap-1">
            <Clock className="w-3 h-3" />
            {job.min_years_experience}+ años experiencia
          </Badge>
        )}
        {job.education_level && (
          <Badge variant="secondary" className="gap-1">
            <GraduationCap className="w-3 h-3" />
            {job.education_level}
          </Badge>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={() => handleOptimizeCV(false)}
          disabled={optimizing}
          variant="outline"
          className="gap-2 flex-1"
        >
          {optimizing ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          {optimizing ? "Analizando..." : 
           optimizationStatus.isApproved ? "Ver mi optimización aprobada" :
           optimization ? "Ver previsualización" : "Optimizar mi CV para este puesto"}
        </Button>
        
        <Button
          onClick={handleApply}
          disabled={applying || hasApplied}
          className="gap-2 flex-1"
        >
          {applying ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : hasApplied ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {applying ? "Enviando..." : hasApplied ? "Ya aplicaste" : "Aplicar ahora"}
        </Button>
      </div>

      {/* No profile warning */}
      {user && !candidateProfile?.cv_data && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-yellow-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-800">
                  Sube tu CV para poder aplicar
                </p>
                <p className="text-xs text-yellow-600">
                  Ve a tu perfil y sube tu CV para desbloquear todas las funciones
                </p>
              </div>
              <Link to="/candidate/profile">
                <Button size="sm" variant="outline">
                  Ir a mi perfil
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Description */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Descripción del puesto</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground whitespace-pre-line">
            {job.description || "Sin descripción disponible"}
          </p>
        </CardContent>
      </Card>

      {/* Required Skills */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Habilidades requeridas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {job.required_skills.map((skill) => (
              <Badge key={skill} className="bg-primary/10 text-primary hover:bg-primary/20">
                {skill}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Optional Skills */}
      {job.optional_skills?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Habilidades deseables</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {job.optional_skills.map((skill) => (
                <Badge key={skill} variant="outline">
                  {skill}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* CV Optimization Dialog */}
      <Dialog open={showOptimization} onOpenChange={setShowOptimization}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Optimización de CV para: {job.title}
            </DialogTitle>
            <DialogDescription>
              {optimizationStatus.isApproved ? (
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  Optimización aprobada
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Previsualización - 
                  <Badge variant="outline" className="text-xs">
                    {optimizationStatus.attemptsRemaining} intentos restantes
                  </Badge>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {optimization && (
            <div className="space-y-6">
              {/* Match Score */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <span className="font-medium">Tu compatibilidad con este puesto</span>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-heading font-bold text-primary">
                    {optimization.match_score}%
                  </span>
                  <Badge className={
                    optimization.match_level === "Alto" ? "bg-green-100 text-green-700" :
                    optimization.match_level === "Medio" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-700"
                  }>
                    {optimization.match_level}
                  </Badge>
                </div>
              </div>

              {/* Strengths */}
              {optimization.strengths?.length > 0 && (
                <div>
                  <h4 className="font-heading font-semibold mb-2 text-green-700">
                    ✓ Tus fortalezas
                  </h4>
                  <ul className="space-y-1">
                    {optimization.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Gaps */}
              {optimization.gaps?.length > 0 && (
                <div>
                  <h4 className="font-heading font-semibold mb-2 text-yellow-700">
                    ⚠ Áreas a mejorar
                  </h4>
                  <ul className="space-y-1">
                    {optimization.gaps.map((g, i) => (
                      <li key={i} className="text-sm text-muted-foreground">• {g}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Optimized Summary */}
              {optimization.optimized_summary && (
                <div>
                  <h4 className="font-heading font-semibold mb-2">
                    📝 Resumen optimizado sugerido
                  </h4>
                  <p className="text-sm bg-primary/5 p-3 rounded-lg border border-primary/20">
                    {optimization.optimized_summary}
                  </p>
                </div>
              )}

              {/* Recommendations */}
              {optimization.recommendations?.length > 0 && (
                <div>
                  <h4 className="font-heading font-semibold mb-2">
                    💡 Recomendaciones específicas
                  </h4>
                  <div className="space-y-2">
                    {optimization.recommendations.map((rec, i) => (
                      <div key={i} className="text-sm p-3 bg-muted rounded-lg">
                        <span className="font-medium text-primary">{rec.section}:</span>
                        {rec.original && (
                          <p className="text-xs text-muted-foreground mt-1 italic">
                            Original: {rec.original}
                          </p>
                        )}
                        <p className="text-muted-foreground mt-1">{rec.suggestion}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Keywords */}
              {optimization.keywords_to_add?.length > 0 && (
                <div>
                  <h4 className="font-heading font-semibold mb-2">
                    🔑 Palabras clave a destacar
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {optimization.keywords_to_add.map((kw, i) => (
                      <Badge key={i} variant="outline">{kw}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Skills to highlight */}
              {optimization.skills_to_highlight?.length > 0 && (
                <div>
                  <h4 className="font-heading font-semibold mb-2">
                    ⭐ Habilidades a destacar
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {optimization.skills_to_highlight.map((skill, i) => (
                      <Badge key={i} className="bg-green-100 text-green-700">{skill}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Attempts indicator */}
              {!optimizationStatus.isApproved && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 text-blue-500" />
                  <span className="text-blue-700">
                    Intentos usados: {optimizationStatus.attemptsUsed} de 3
                  </span>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {!optimizationStatus.isApproved && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => handleOptimizeCV(true)}
                  disabled={optimizing || optimizationStatus.attemptsRemaining <= 0}
                  className="gap-2"
                >
                  {optimizing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Regenerar ({optimizationStatus.attemptsRemaining} restantes)
                </Button>
                <Button 
                  onClick={handleApproveOptimization}
                  disabled={approving}
                  className="gap-2"
                >
                  {approving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ThumbsUp className="w-4 h-4" />
                  )}
                  Aprobar esta optimización
                </Button>
              </>
            )}
            {optimizationStatus.isApproved && (
              <Button onClick={() => setShowOptimization(false)}>
                Cerrar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
